import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, useColumnVisibility, type ColumnDefinition,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useVoucherTypes } from 'hooks/api/useAccounts';
import { voucherTypeService } from 'api/services';
import type { VoucherTypeDto, VoucherTypeQueryParams } from 'api/types';
import { VoucherTypeFormModal, VoucherTypeViewModal } from '../../../modals/modules/accounts/VoucherTypeFormModal';

// ============================================================================
// SCREEN
// ============================================================================

type ViewMode = 'list' | 'grid';

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'voucherCode', label: 'Code' },
  { id: 'voucherName', label: 'Name' },
  { id: 'voucherClassName', label: 'Class' },
  { id: 'numberingMethod', label: 'Numbering' },
  { id: 'resetFrequency', label: 'Reset' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface VoucherTypesScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const VoucherTypesScreen: React.FC<VoucherTypesScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('voucher-types');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('voucher-types-columns', COLUMN_DEFS);

  const [queryParams, setQueryParams] = useState<VoucherTypeQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useVoucherTypes(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [classFilter, setClassFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<VoucherTypeDto | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');

  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const [copyInstanceId, setCopyInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handlePageChange = useCallback((idx: number) => setQueryParams(p => ({ ...p, page: idx + 1 })), []);
  const handlePageSizeChange = useCallback((size: number) => setQueryParams(p => ({ ...p, pageSize: size, page: 1 })), []);

  const handleAddNew = useCallback(() => {
    let next = 1;
    if (tabId) {
      while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelectedRecord(null); setFormModalOpen(true);
  }, [tabId, isMinimized]);

  const handleView = useCallback((id: string) => {
    const record = data.find(v => v.id === id);
    if (!record) return;
    const viewId = tabId ? `${tabId}-view-modal-${record.voucherCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setSelectedRecord(record); setViewModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleEdit = useCallback((id: string) => {
    const record = data.find(v => v.id === id);
    if (!record) return;
    const modalId = tabId ? `${tabId}-form-modal-edit-${record.voucherCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelectedRecord(record); setFormModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((id: string) => {
    const record = data.find(v => v.id === id);
    if (!record) return;
    const modalId = tabId ? `${tabId}-form-modal-copy-${record.voucherCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    if (formModalOpen) formModalMinimizeRef.current?.();
    setCopyInstanceId(record.voucherCode);
    setFormMode('copy'); setSelectedRecord(record); setFormModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal, formModalOpen]);

  const handleDelete = useCallback(async (id: string) => {
    const record = data.find(v => v.id === id);
    if (!record) return;
    const ok = await confirmDelete({ resourceLabel: record.voucherName });
    if (!ok) return;
    try {
      await voucherTypeService.delete(record.id, record.rowVersion);
      notificationService.toast.success(`Voucher type "${record.voucherName}" deleted.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'An error occurred while deleting.');
    }
  }, [data, confirmDelete, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false); setSelectedRecord(null); refetch();
  }, [refetch]);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, voucherCode?: string) => {
      if (instanceId) {
        if (restoredMode === 'copy') setCopyInstanceId(instanceId);
        else setCreateInstanceId(instanceId);
      }
      if (voucherCode) {
        const rec = data.find(v => v.voucherCode === voucherCode);
        if (rec) setSelectedRecord(rec);
      }
      setFormMode(restoredMode); setFormModalOpen(true);
    },
    [data],
  );

  const handleRestoreView = useCallback((voucherCode: string) => {
    const rec = data.find(v => v.voucherCode === voucherCode);
    if (rec) { setSelectedRecord(rec); setViewModalOpen(true); }
  }, [data]);

  const tableColumns: SyutGridColumn<VoucherTypeDto>[] = useMemo(() => [
    { id: 'voucherCode', header: 'Code', accessorKey: 'voucherCode', size: 130 },
    { id: 'voucherName', header: 'Name', accessorKey: 'voucherName', size: 220 },
    {
      id: 'voucherClassName', header: 'Class', accessorKey: 'voucherClassName', size: 170,
      cell: ({ row }) => (row.original as VoucherTypeDto).voucherClassName || (row.original as VoucherTypeDto).voucherClass,
    },
    { id: 'numberingMethod', header: 'Numbering', accessorKey: 'numberingMethod', size: 110 },
    { id: 'resetFrequency', header: 'Reset', accessorKey: 'resetFrequency', size: 100 },
    {
      id: 'status', header: 'Status', accessorKey: 'statusName', size: 130,
      cell: ({ row }) => {
        const r = row.original as VoucherTypeDto;
        return (
          <span style={{ display: 'inline-flex', gap: '0.375rem' }}>
            <span className="badge"
              style={r.statusColor ? { background: r.statusColor, color: '#fff' } : undefined}>
              {r.statusName || 'Active'}
            </span>
            {r.isSystem && <span className="badge">System</span>}
          </span>
        );
      },
    },
  ], []);

  const getRowActions = useCallback((rec: VoucherTypeDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(rec.id) },
    ];
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(rec.id) });
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(rec.id) });
    }
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(rec.id), variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAddNew, 'Add Voucher Type'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const loadClassOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const classes = await voucherTypeService.getClasses();
      const filtered = input
        ? classes.filter(c => c.label.toLowerCase().includes(input.toLowerCase()))
        : classes;
      return [{ value: '', label: 'All Classes' }, ...filtered.map(c => ({ value: c.value, label: c.label }))];
    } catch {
      return [{ value: '', label: 'All Classes' }];
    }
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      voucherClass: classFilter || undefined,
      numberingMethod: methodFilter || undefined,
      voucherCode: codeFilter || undefined,
      voucherName: nameFilter || undefined,
      includeInactive: includeInactive || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [classFilter, methodFilter, codeFilter, nameFilter, includeInactive]);

  const clearFilters = useCallback(() => {
    setClassFilter(''); setMethodFilter(''); setCodeFilter(''); setNameFilter(''); setIncludeInactive(false);
    setQueryParams(prev => ({ page: 1, pageSize: prev.pageSize }));
  }, []);

  const appliedCount = [classFilter, methodFilter, codeFilter, nameFilter, includeInactive ? '1' : '']
    .filter(Boolean).length;

  return (
    <EnterpriseScreenShell
      title="Voucher Types"
      description="Document types (Sales Invoice, Payment, Journal…) with numbering and posting behaviour"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search voucher types...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Class" isAsync loadOptions={loadClassOptions} defaultOptions cacheOptions={false}
                  value={classFilter} onChange={(v) => setClassFilter((v as string) || '')}
                  placeholder="All Classes" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Numbering"
                  options={[
                    { value: '', label: 'All' },
                    { value: 'AUTO', label: 'Auto' },
                    { value: 'MANUAL', label: 'Manual' },
                  ]}
                  value={methodFilter} onChange={(v) => setMethodFilter((v as string) || '')}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
                <input type="text" className="form-input" value={codeFilter}
                  onChange={(e) => setCodeFilter(e.target.value)}
                  placeholder="Filter by code..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Name</label>
                <input type="text" className="form-input" value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Filter by name..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)} /> Include inactive
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={tableColumns} enableRowSelection
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange}
            rowActions={getRowActions}
            emptyMessage={loading ? 'Loading...' : 'No voucher types found'}
            frozenLeftColumns={['select', 'voucherCode']} frozenRightColumns={['actions']}
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <IconSystem.file size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <p>{loading ? 'Loading...' : 'No voucher types found'}</p>
                </div>
              ) : (
                data.map((v) => (
                  <div key={v.id} style={{
                    background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                    borderRadius: '0.75rem', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{v.voucherCode}</div>
                        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0.25rem 0 0' }}>{v.voucherName}</h3>
                      </div>
                      <ActionMenu items={getRowActions(v)} />
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{v.voucherClassName || v.voucherClass}</div>
                    <div style={{
                      display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)',
                      paddingTop: '0.5rem', borderTop: '1px solid var(--border-color)', flexWrap: 'wrap',
                    }}>
                      <span>{v.numberingMethod}</span>
                      <span className="badge" style={{ marginLeft: 'auto' }}>{v.statusName || 'Active'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            {data.length > 0 && (
              <div className="pagination-footer">
                <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <select value={queryParams.pageSize ?? 25}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem' }}>
                    {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size} / page</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <VoucherTypeFormModal isOpen={formModalOpen} mode={formMode} voucherType={selectedRecord}
        onSave={handleSaveSuccess}
        onClose={() => { setFormModalOpen(false); setSelectedRecord(null); }}
        openedBy="VoucherTypesScreen"
        tabId={tabId} moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId} copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef} />

      <VoucherTypeViewModal isOpen={viewModalOpen} voucherType={selectedRecord}
        onClose={() => { setViewModalOpen(false); setSelectedRecord(null); }}
        onEdit={(rec) => handleEdit(rec.id)}
        tabId={tabId} moduleName={moduleName}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default VoucherTypesScreen;
